/**
 * Dynamic Select Fields Handler
 *
 * Handles cascading select updates for program forms
 *
 * @package DesignSchool\Forms
 */

import { apiClient } from './api-client';

export class DynamicSelects {
    private $: any;
    private cache: Map<string, any> = new Map();

    constructor() {
        this.$ = jQuery;
        this.init();
    }

    /**
     * Initialize dynamic selects
     */
    private init(): void {
        this.$(document).on('form-loaded', () => {
            this.loadAllCategories();
        });

        this.$(document).ready(() => {
            setTimeout(() => this.loadAllCategories(), 100);
        });

        this.$(document).on('change', '.form-select-category', (e: Event) => {
            const $select = this.$(e.target);
            const categorySlug = $select.val() as string;
            const type = $select.data('type') || 'och';

            const $programField = $select.closest('.ds-form').find(`.form-select-program[data-type="${type}"]`);

            if (categorySlug && $programField.length) {
                this.loadPrograms(type, categorySlug, $programField);
            } else {
                $select.closest('.ds-form').find(`.form-select-program[data-type="${type}"]`).each((_: number, el: HTMLElement) => {
                    this.clearPrograms(this.$(el));
                });
            }
        });
    }

    /**
     * Collect parent .ds-form nodes for every category select (preselect lives on the form, not globally)
     */
    private getFormRootsForCategorySelects(): any[] {
        const roots: HTMLElement[] = [];
        const seen = new Set<HTMLElement>();
        this.$('.form-select-category').each((_: number, el: HTMLElement) => {
            const formEl = this.$(el).closest('.ds-form').get(0) as HTMLElement | undefined;
            if (formEl && !seen.has(formEl)) {
                seen.add(formEl);
                roots.push(formEl);
            }
        });
        return roots.map((el) => this.$(el));
    }

    /**
     * Load categories for all dynamic program forms on the page
     */
    private async loadAllCategories(): Promise<void> {
        const $forms = this.getFormRootsForCategorySelects();
        if (!$forms.length) {
            return;
        }
        await Promise.all($forms.map(($f: any) => this.loadCategoriesForForm($f)));
    }

    /**
     * Load categories and dependent programs for one form (uses that form's data-preselect-* only)
     */
    private async loadCategoriesForForm($form: any): Promise<void> {
        const $categorySelects = $form.find('.form-select-category');
        if (!$categorySelects.length) {
            return;
        }

        // options and selected values already rendered in PHP (send_bitum-style)
        let serverRendered = true;
        $categorySelects.each((_: number, el: HTMLElement) => {
            if (this.$(el).find('option').length === 0) {
                serverRendered = false;
            }
        });
        if (serverRendered) {
            return;
        }

        const preselectType = $form.attr('data-preselect-type');
        const preselectCategory = $form.attr('data-preselect-category');
        const preselectProgram = $form.attr('data-preselect-program');
        const hasPreselect = !!(preselectType && preselectCategory && preselectProgram);

        const types = new Set<string>();
        $categorySelects.each((_: number, el: HTMLElement) => {
            types.add(this.$(el).data('type') || 'och');
        });

        for (const type of types) {
            try {
                const categories = await apiClient.getCategories(type);
                const $selects = $categorySelects.filter(`[data-type="${type}"]`);
                const usePreselect =
                    hasPreselect &&
                    type === preselectType &&
                    categories.some((c: any) => c.slug === preselectCategory);

                $selects.each((_: number, el: HTMLElement) => {
                    const $select = this.$(el);
                    const autoFirstCategory = !usePreselect;
                    this.updateSelect($select, categories, autoFirstCategory, usePreselect ? preselectCategory : undefined);

                    const categorySlug = usePreselect
                        ? preselectCategory
                        : categories.length > 0
                          ? categories[0].slug
                          : '';

                    if (categorySlug) {
                        const $programField = $select.closest('.ds-form').find(`.form-select-program[data-type="${type}"]`);
                        if ($programField.length) {
                            const programToSelect = usePreselect && preselectProgram ? preselectProgram : undefined;
                            this.loadPrograms(type, categorySlug, $programField, programToSelect);
                        }
                    }
                });
            } catch (error) {
                console.error(`[DynamicSelects] Error loading categories for ${type}:`, error);
            }
        }
    }

    /**
     * Load programs for category
     * @param selectSlug If set, select this program; otherwise first item when list non-empty
     */
    private async loadPrograms(type: string, categorySlug: string, $target: any, selectSlug?: string): Promise<void> {
        const cacheKey = `${type}_${categorySlug}`;

        if (this.cache.has(cacheKey)) {
            const autoFirst = selectSlug === undefined || selectSlug === '';
            this.updateSelect($target, this.cache.get(cacheKey), autoFirst, selectSlug || undefined);
            return;
        }

        try {
            $target.prop('disabled', true);
            this.showLoadingState($target);

            const programs = await apiClient.getPrograms(type, categorySlug);
            this.cache.set(cacheKey, programs);

            const autoFirst = selectSlug === undefined || selectSlug === '';
            this.updateSelect($target, programs, autoFirst, selectSlug || undefined);
        } catch (error) {
            console.error('[DynamicSelects] Error loading programs:', error);
            $target.empty();
            $target.append(this.$('<option>').val('').text('Ошибка загрузки'));
        } finally {
            $target.prop('disabled', false);
        }
    }

    private showLoadingState($select: any): void {
        $select.empty();
        $select.append(this.$('<option>').val('').text('Загрузка...').prop('disabled', true));
    }

    /**
     * Replace options. Placeholder row only if items is empty (or error text appended separately).
     */
    private updateSelect($select: any, items: any[], autoSelectFirst: boolean, selectSlug?: string): void {
        $select.empty();

        if (!items || items.length === 0) {
            $select.append(this.$('<option>').val('').text('Нет вариантов'));
            return;
        }

        let matchedSlug = false;
        items.forEach((item: any, index: number) => {
            const slug = item.slug ?? '';
            const $option = this.$('<option>').val(slug).text(item.name ?? '');
            const pickBySlug = selectSlug && slug === selectSlug;
            if (pickBySlug) {
                matchedSlug = true;
                $option.prop('selected', true);
            } else if (!selectSlug && autoSelectFirst && index === 0) {
                $option.prop('selected', true);
            }
            $select.append($option);
        });

        if (selectSlug && !matchedSlug && items.length > 0) {
            $select.find('option').first().prop('selected', true);
        }
    }

    /**
     * Clear programs select (empty list)
     */
    private clearPrograms($select: any): void {
        $select.empty();
        $select.append(this.$('<option>').val('').text('Нет вариантов'));
    }

    /**
     * Clear cache
     */
    public clearCache(): void {
        this.cache.clear();
    }
}

declare module './api-client' {
    interface ApiClient {
        getPrograms(type: string, categorySlug: string): Promise<any[]>;
    }
}

(apiClient as any).getPrograms = async function (type: string, categorySlug: string): Promise<any[]> {
    const response = await this.request(`programs/${type}/${categorySlug}`);
    return response || [];
};

export const dynamicSelects = new DynamicSelects();
